import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  IconButton,
} from '@mui/material';
import { cn } from 'cn';
import { type ComponentProps, forwardRef, type ReactNode } from 'react';

import { Spinner } from '../Spinner';
import { Tooltip } from '../Tooltip';

export type ButtonProps = MuiButtonProps & {
  readonly buttonStyle?: 'primary' | 'secondary' | 'link';
  readonly icon?: ReactNode;
  readonly inProgress?: boolean;
  /** Anchor rel — MUI renders the button as an <a> when `href` is set. */
  readonly rel?: string;
  /** Anchor target — MUI renders the button as an <a> when `href` is set. */
  readonly target?: string;
  readonly text?: string;
  readonly title?: string;
} & ComponentStyling;

// Temporary interface helper function to ease migration from Material UI
const buttonStyleToVariant = (
  buttonStyle: ButtonProps['buttonStyle'],
): ButtonProps['variant'] => {
  switch (buttonStyle) {
    case 'primary': {
      return 'contained';
    }

    case 'secondary': {
      return 'outlined';
    }

    case 'link': {
      return 'text';
    }

    default: {
      return 'contained';
    }
  }
};

const WRAPPER_LAYOUT_CLASS =
  /^(?:[^\s:]+:)*(?:w-full|flex-1|grow|self-stretch|m[trbl]?-auto)$/;

/**
 * A disabled button is wrapped in a focusable span so the tooltip can still
 * open. The span becomes the flex/grid child, so the layout classes that
 * size the button (`w-full`, `flex-1`, ...) must be repeated on it or the
 * button would shrink to its content. Only these classes are forwarded,
 * including when conditioned by variants (`md:w-full`, `last:ml-auto`).
 */
const includeWrapperLayoutClasses = (className?: string) => {
  return className
    ?.split(/\s+/)
    .filter((token) => WRAPPER_LAYOUT_CLASS.test(token));
};

function renderWithTooltip(
  button: ComponentProps<typeof Tooltip>['children'],
  title: string | undefined,
  disabled: boolean,
  className?: string,
) {
  if (!title) {
    return button;
  }

  if (disabled) {
    return (
      <Tooltip content={title} openOnFocus>
        <span
          className={cn('inline-flex', includeWrapperLayoutClasses(className))}
          tabIndex={0}
        >
          {button}
        </span>
      </Tooltip>
    );
  }

  return <Tooltip content={title}>{button}</Tooltip>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      buttonStyle = 'primary',
      children,
      className,
      color = 'primary',
      disabled = false,
      icon,
      inProgress = false,
      onClick,
      style,
      text,
      title,
      variant,
      ...rest
    },
    ref,
  ) {
    const buttonProps = {
      className: cn(
        'h-10 cursor-pointer! whitespace-nowrap',
        {
          'bg-white': buttonStyle === 'secondary',
        },
        className,
      ),
      color,
      disabled: disabled || inProgress,
      onClick,
      style,
      variant: variant ?? buttonStyleToVariant(buttonStyle),
      ...rest,
    };

    const ButtonComponent =
      icon && !text ? (
        <IconButton {...buttonProps} ref={ref}>
          {inProgress ? <Spinner color="inherit" /> : icon}
        </IconButton>
      ) : (
        <MuiButton
          {...buttonProps}
          ref={ref}
          startIcon={inProgress ? <Spinner color="inherit" /> : icon}
        >
          {/* Show the full label as a native title only when the text is actually
            truncated. Checked on mouseEnter, which is in time because native
            title tooltips only surface on hover. */}
          <span
            className="truncate"
            onMouseEnter={
              title
                ? undefined
                : (event) => {
                    const element = event.currentTarget;
                    if (element.scrollWidth > element.clientWidth) {
                      element.title = text ?? '';
                    } else {
                      element.removeAttribute('title');
                    }
                  }
            }
          >
            {text}
            {children}
          </span>
        </MuiButton>
      );

    return renderWithTooltip(
      ButtonComponent,
      title,
      disabled || inProgress,
      className,
    );
  },
);
